import { ExternalLink, Loader2 } from "lucide-react";

import { Id } from "@cvx/_generated/dataModel";
import { api } from "@cvx/_generated/api";
import { convexQuery } from "@convex-dev/react-query";
import { useQuery } from "@tanstack/react-query";

interface ApplicationVideoLinkProps {
  storageId: Id<"_storage"> | string;
  index: number;
  className?: string;
}

export default function ApplicationVideoLink({
  storageId,
  index,
  className = "inline-flex items-center gap-1 text-xs text-primary/60 hover:text-primary",
}: ApplicationVideoLinkProps) {
  // Check if this is a legacy string URL
  const isLegacyUrl =
    typeof storageId === "string" && storageId.startsWith("http");

  const { data: videoUrl, isLoading } = useQuery({
    ...convexQuery(api.applications.getApplicationVideoUrl, {
      storageId: storageId as Id<"_storage">,
    }),
    enabled: !isLegacyUrl, // Only fetch if it's not a legacy URL
  });

  // Handle legacy URLs directly
  if (isLegacyUrl) {
    return (
      <a
        href={storageId as string}
        target="_blank"
        rel="noopener noreferrer"
        className={className}
      >
        <ExternalLink className="h-3 w-3" />
        Video {index + 1}
      </a>
    );
  }

  if (isLoading) {
    return (
      <div className={className}>
        <Loader2 className="h-3 w-3 animate-spin" />
        Loading video {index + 1}...
      </div>
    );
  }

  if (!videoUrl) {
    return (
      <div
        className={className.replace(
          "hover:text-primary",
          "text-muted-foreground",
        )}
      >
        <ExternalLink className="h-3 w-3" />
        Video {index + 1} (unavailable)
      </div>
    );
  }

  return (
    <a
      href={videoUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={className}
      download
    >
      <ExternalLink className="h-3 w-3" />
      Video {index + 1}
    </a>
  );
}
